/**
 * CustomHtmlTemplatesPanel — choose which published custom HTML template a page uses.
 * Mounted by All Journeys on two tabs:
 *
 *   scope "events"  · Event List Page — the tenant's event-listing slot. Standard
 *                     (the page designed on this tab) or a published custom `page`.
 *                     Written by setPortalEventListTemplate; null restores Standard.
 *   scope "landing" · Event Landing Page — the tenant default detail template
 *                     (settings PUT) or ONE real event's own template (event PATCH,
 *                     only that event changes; clearing it inherits the default).
 *
 * Data: bound, through ./template-preview-controller — listPortalExperiencePages ·
 * getPortalSettings · setPortalEventListTemplate · putPortalSettings · getStudioEvent ·
 * updateEvent (creation) · previewPortalExperiencePage.
 *
 * The panel is a VIEW of the Designer's controller: the scope, event and unapplied pick live
 * there, so leaving this settings section (or switching the phone layout to the preview)
 * keeps them. "Show in preview" puts the choice in the Designer's right-hand preview,
 * labelled as not applied until it is. A choice is never applied as you pick it: Apply
 * snapshots exactly what will change (scope, event, template), asks for confirmation of that
 * snapshot, sends it, then reads the canonical value back and compares; only a matching
 * read-back is reported as done. While it runs, the scope, event and template controls are
 * locked. A failure keeps the choice on screen.
 */
import React from "react";
import {
  Banner, Button, ConfirmDialog, Dropdown, Field, InlineEmptyState, Section, SegmentedControl, Skeleton, Spinner, StatusPill,
} from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../../lib/i18n";
import { useUser } from "../../../shell/access";
import { useNav } from "../../../shell/router";
import { listCarriesLiveLayout, templateCapabilities, tf } from "./template-data";
import {
  INHERIT_VALUE, STANDARD_VALUE, TemplatePreviewController, useTemplatePreviewController,
  type ApplySnapshot, type TemplateTarget,
} from "./template-preview-controller";
import { TemplateLibrary } from "./TemplateLibrary";
import { visitorsSeeLabel } from "./CustomTemplateCanvas";

export interface CustomHtmlTemplatesPanelProps {
  scope: "events" | "landing";
  /** The Designer's controller, which outlives this panel. Without one the panel keeps its own. */
  controller?: TemplatePreviewController | null;
  /** Bring the right-hand preview on screen (the phone and mid layouts show one pane at a time). */
  onShowPreview?: () => void;
}

export function CustomHtmlTemplatesPanel(props: CustomHtmlTemplatesPanelProps) {
  useUser(); // re-render when access changes
  const caps = templateCapabilities();
  const nav = useNav();
  const [own] = React.useState<TemplatePreviewController | null>(() => (props.controller ? null : new TemplatePreviewController()));
  const ctrl = (props.controller || own) as TemplatePreviewController;
  const st = useTemplatePreviewController(ctrl) || ctrl.getState();
  const [confirm, setConfirm] = React.useState<ApplySnapshot | null>(null);
  const [libraryOpen, setLibraryOpen] = React.useState(false);

  React.useEffect(() => { ctrl.activate(props.scope); }, [ctrl, props.scope]);
  /* The template library previews event pages with the same catalogue, on any tab. */
  React.useEffect(() => {
    if (libraryOpen && st.catalog.status === "idle") ctrl.loadCatalog();
  }, [libraryOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = ctrl.view();
  const sel = view.selection;
  const target: TemplateTarget = props.scope === "events" ? "list" : st.landing.scope === "default" ? "default" : "event";
  const kind = props.scope === "events" ? "page" : "event-detail";
  const candidates = ctrl.candidates();
  const titleOf = ctrl.titleOf;
  const eligible = (slug: string | null | undefined) => ctrl.eligible(slug);
  const eventsList = st.catalog.events;
  const eventId = st.landing.eventId;
  const eventRead = eventId ? st.events[eventId] : undefined;

  const STANDARD_LIST = i18nT("journeys.customTemplates.standardList", "Standard event list");
  const NO_TEMPLATE = i18nT("journeys.customTemplates.noTemplate", "No template");
  const INHERIT_LABEL = i18nT("journeys.customTemplates.inheritDefault", "Use the default for all events");
  const emptyValueFor = (t: TemplateTarget) => (t === "event" ? INHERIT_VALUE : STANDARD_VALUE);
  const labelOf = (v: string, t: TemplateTarget): string =>
    v === STANDARD_VALUE ? (t === "list" ? STANDARD_LIST : NO_TEMPLATE) : v === INHERIT_VALUE ? INHERIT_LABEL : (titleOf(v) || v);
  const describe = (slug: string | null, t: TemplateTarget) => (slug ? (titleOf(slug) || slug) : labelOf(emptyValueFor(t), t));

  const current = sel && sel.target === target ? sel.appliedSlug : null;
  const value = sel && sel.target === target ? sel.value : emptyValueFor(target);
  const changed = !!sel && sel.target === target && sel.candidate;

  const canApply = target === "list" ? caps.chooseListTemplate
    : target === "default" ? caps.chooseDefaultDetailTemplate : caps.chooseEventDetailTemplate;
  const applying = st.applying;
  const locked = !!applying;

  const options: Array<{ value: string; label: string; supportingText?: string; disabled?: boolean }> = [
    target === "list"
      ? { value: STANDARD_VALUE, label: STANDARD_LIST, supportingText: i18nT("journeys.customTemplates.standardListHint", "The page designed on this tab") }
      : target === "default"
        ? { value: STANDARD_VALUE, label: NO_TEMPLATE, supportingText: i18nT("journeys.customTemplates.noTemplateHint", "Events use the event page designed on this tab") }
        : { value: INHERIT_VALUE, label: INHERIT_LABEL, supportingText: titleOf(st.settings?.events.detailTemplate) || i18nT("journeys.customTemplates.noTemplateHint", "Events use the event page designed on this tab") },
    ...candidates.map((c) => ({ value: c.slug, label: c.title, supportingText: "/" + c.slug })),
  ];
  /* A stored choice that is no longer a live template still shows, marked, so it is never silently re-labelled. */
  if (current && !eligible(current)) {
    options.push({ value: current, label: titleOf(current) || current, supportingText: i18nT("journeys.customTemplates.notPublishedNow", "Not published right now"), disabled: true });
  }

  const askToApply = () => {
    const snap = ctrl.applySnapshot(labelOf);
    if (!snap) return;
    ctrl.clearApplyError();
    setConfirm(snap);
  };

  const showInPreview = () => {
    ctrl.setView("custom");
    props.onShowPreview?.();
  };

  const confirmCopy = (snap: ApplySnapshot): { title: string; body: string } => {
    if (snap.target === "list") {
      return {
        title: i18nT("journeys.customTemplates.confirmListTitle", "Change the event list?"),
        body: snap.slug === null
          ? i18nT("journeys.customTemplates.confirmListStandard", "Visitors to your event list see the page designed on this tab again, straight away.")
          : tf("journeys.customTemplates.confirmList", "Visitors to your event list see “{name}” straight away. The page designed on this tab stays saved and comes back if you choose the standard list again.", { name: snap.label }),
      };
    }
    if (snap.target === "default") {
      return {
        title: i18nT("journeys.customTemplates.confirmDefaultTitle", "Change the template for all events?"),
        body: snap.slug === null
          ? i18nT("journeys.customTemplates.confirmDefaultNone", "Events without their own template go back to the event page designed on this tab, straight away. Events with their own template keep it.")
          : tf("journeys.customTemplates.confirmDefault", "Every event without its own template uses “{name}” straight away. Events with their own template keep it.", { name: snap.label }),
      };
    }
    return {
      title: tf("journeys.customTemplates.confirmEventTitle", "Change the template for {event}?", { event: snap.eventName }),
      body: snap.slug === null
        ? tf("journeys.customTemplates.confirmEventInherit", "“{event}” goes back to the default for all events, straight away. Nothing else changes.", { event: snap.eventName })
        : tf("journeys.customTemplates.confirmEvent", "“{event}” uses “{name}” straight away. The default and every other event stay as they are.", { event: snap.eventName, name: snap.label }),
    };
  };

  /* ── Render ─────────────────────────────────────────────────────────── */

  const sectionActions = caps.author ? (
    <Button size="sm" hierarchy="tertiary" disabled={locked} onClick={() => setLibraryOpen(true)}>{i18nT("journeys.customTemplates.manage", "Template library")}</Button>
  ) : undefined;

  const loadingRows = (
    <div role="status" aria-label={i18nT("journeys.customTemplates.loading", "Loading templates")} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="rect" width="100%" height={40} />
      <Skeleton variant="rect" width={168} height={32} />
    </div>
  );

  let body: React.ReactNode;
  if (st.data.status === "error" && !st.settings) {
    body = (
      <Banner tone="danger" title={i18nT("journeys.customTemplates.loadFailed", "Templates couldn't be loaded")}
        action={<Button size="sm" hierarchy="secondary" onClick={ctrl.refresh}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
        {st.data.message}
      </Banner>
    );
  } else if (!st.settings) {
    body = loadingRows;
  } else {
    const eventPending = target === "event" && !(eventRead && (eventRead.status === "ready" || eventRead.data));
    const statusTitle = current ? (titleOf(current) || current)
      : target === "event" ? i18nT("journeys.customTemplates.inheritsDefault", "Uses the default for all events")
        : target === "list" ? STANDARD_LIST : NO_TEMPLATE;
    const applyingHere = !!applying && applying.target === target && (target !== "event" || applying.eventId === eventId);
    const errorHere = st.applyError && st.applyError.snapshot.target === target && (target !== "event" || st.applyError.snapshot.eventId === eventId) ? st.applyError : null;
    const needsEventForPreview = props.scope === "landing" && !sel?.event;
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}>
        {props.scope === "landing" ? (
          <SegmentedControl
            full
            ariaLabel={i18nT("journeys.customTemplates.applyTo", "Apply to")}
            options={[
              { value: "default", label: i18nT("journeys.customTemplates.allEvents", "All events") },
              { value: "event", label: i18nT("journeys.customTemplates.oneEvent", "One event") },
            ]}
            value={st.landing.scope}
            onChange={(v) => ctrl.setLandingScope(v === "event" ? "event" : "default")}
          />
        ) : null}
        {st.data.status === "error" ? (
          <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.loadFailed", "Templates couldn't be loaded")}
            action={<Button size="sm" hierarchy="secondary" onClick={ctrl.refresh}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
            {st.data.message}
          </Banner>
        ) : null}
        {props.scope === "landing" && st.catalog.status === "error" ? (
          <Banner tone="danger" title={i18nT("journeys.customTemplates.catalogFailed", "Events couldn't be loaded")}
            action={<Button size="sm" hierarchy="secondary" onClick={ctrl.loadCatalog}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
            {st.catalog.message}
          </Banner>
        ) : null}
        {props.scope === "landing" && (st.catalog.status === "loading" || st.catalog.status === "idle") && !eventsList.length ? loadingRows
          : props.scope === "landing" && st.catalog.status === "error" && !eventsList.length ? null
          : props.scope === "landing" ? (
          eventsList.length ? (
            <Field label={target === "event"
              ? i18nT("journeys.customTemplates.event", "Event")
              : i18nT("journeys.customTemplates.exampleEvent", "Example event for the preview")}>
              <Dropdown searchable value={eventId || null} disabled={locked} onChange={(v) => ctrl.setEventId(v)}
                aria-label={target === "event"
                  ? i18nT("journeys.customTemplates.event", "Event")
                  : i18nT("journeys.customTemplates.exampleEvent", "Example event for the preview")}
                options={eventsList.map((e) => ({ value: e.id, label: e.name, supportingText: e.when }))} />
            </Field>
          ) : (
            <InlineEmptyState title={i18nT("journeys.customTemplates.noEvents", "No published events")} description={i18nT("journeys.customTemplates.noEventsHint", "Publish an event to give it its own template.")} />
          )
        ) : null}
        {target === "event" && eventRead && eventRead.status === "error" ? (
          <Banner tone="danger" title={i18nT("journeys.customTemplates.eventLoadFailed", "This event couldn't be loaded")}
            action={<Button size="sm" hierarchy="secondary" onClick={() => ctrl.loadEvent(eventId)}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
            {eventRead.message}
          </Banner>
        ) : null}
        {target === "event" && eventRead && eventRead.status === "loading" && !eventRead.data ? loadingRows : eventPending ? null : (<>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontSize: "13px", color: "var(--jrni-color-text-strong)" }}>
            <span>{i18nT("journeys.customTemplates.inUse", "In use:")}</span>
            <StatusPill tone={current ? "primary" : "neutral"}>{statusTitle}</StatusPill>
          </div>
          {current && !eligible(current) && listCarriesLiveLayout(st.pages) ? (
            <Banner tone="warning" size="compact" title={i18nT("journeys.customTemplates.notLiveTitle", "This template isn't published right now")}>
              {target === "list"
                ? i18nT("journeys.customTemplates.notLiveList", "Visitors see the standard event list until it is published again, or until you choose another template.")
                : i18nT("journeys.customTemplates.notLiveDetail", "Visitors see the fallback page until it is published again, or until you choose another template.")}
            </Banner>
          ) : null}
          {candidates.length === 0 ? (
            <InlineEmptyState size="sm"
              title={i18nT("journeys.customTemplates.noneTitle", "No published templates yet")}
              description={caps.author
                ? i18nT("journeys.customTemplates.noneAuthor", "Create and publish one in the template library.")
                : i18nT("journeys.customTemplates.noneTenant", "Templates are made for you by JRNI. Once one is published, you can choose it here.")} />
          ) : null}
          <Field label={i18nT("journeys.customTemplates.template", "Template")}
            hint={candidates.length === 0 ? undefined : caps.author
              ? i18nT("journeys.customTemplates.pickerHintAuthor", "Choose a published template here. Add, rename or edit templates (HTML and CSS) in Template library.")
              : i18nT("journeys.customTemplates.pickerHintTenant", "Choose a published template here. JRNI template authors manage template names and their HTML and CSS.")}>
            <Dropdown value={value} onChange={(v) => ctrl.setChoice(v)} disabled={locked}
              aria-label={i18nT("journeys.customTemplates.template", "Template")} options={options} />
          </Field>
          {changed ? (
            <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
              {tf("journeys.customTemplates.pickNotApplied", "Not applied yet. Visitors still see {current}.", { current: visitorsSeeLabel(view) })}
            </span>
          ) : null}
          {errorHere ? (
            <Banner tone="danger" title={i18nT("journeys.customTemplates.applyFailed", "Not changed as asked")}
              action={<Button size="sm" hierarchy="secondary" onClick={() => setConfirm(errorHere.snapshot)}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
              {errorHere.message}
            </Banner>
          ) : null}
          {!canApply ? (
            <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
              {i18nT("journeys.customTemplates.readOnly", "You can preview templates. Changing which one is used needs edit access.")}
            </span>
          ) : null}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button size="sm" hierarchy="secondary" disabled={!sel?.effectiveSlug || needsEventForPreview} onClick={showInPreview}>
              {i18nT("journeys.customTemplates.showInPreview", "Show in preview")}
            </Button>
            <Button size="sm" hierarchy="primary" disabled={!canApply || !changed || locked || (value !== STANDARD_VALUE && value !== INHERIT_VALUE && !eligible(value))}
              aria-busy={applyingHere || undefined} iconLeft={applyingHere ? <Spinner size="sm" onColor label={i18nT("journeys.customTemplates.applying", "Applying")} /> : undefined}
              onClick={askToApply}>
              {applyingHere ? i18nT("journeys.customTemplates.applying", "Applying") : i18nT("journeys.customTemplates.apply", "Apply")}
            </Button>
            {changed && !locked ? (
              <Button size="sm" hierarchy="tertiary" onClick={() => { ctrl.setChoice(null); ctrl.clearApplyError(); }}>{i18nT("journeys.customTemplates.undoPick", "Keep current")}</Button>
            ) : null}
            {target === "event" && eventId ? (
              <Button size="sm" hierarchy="tertiary" disabled={locked} onClick={() => nav.go("landing.editor", { id: eventId })}>
                {i18nT("journeys.customTemplates.openEventPage", "Open this event's page")}
              </Button>
            ) : null}
          </div>
          {props.scope === "landing" && sel?.effectiveSlug && needsEventForPreview && st.catalog.status !== "error" ? (
            <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>{st.catalog.status === "loading" || st.catalog.status === "idle"
              ? i18nT("journeys.customTemplates.loadingEvents", "Loading events…")
              : i18nT("journeys.customTemplates.previewNeedsEvent", "A preview needs a published event.")}</span>
          ) : null}
        </>)}
      </div>
    );
  }

  const confirmText = confirm ? confirmCopy(confirm) : null;

  return (
    <>
      <Section
        title={i18nT("journeys.customTemplates.title", "Custom HTML templates")}
        description={props.scope === "events"
          ? i18nT("journeys.customTemplates.listDescription", "Serve your event list with a custom HTML page made for you by JRNI, instead of the standard list.")
          : i18nT("journeys.customTemplates.detailDescription", "Serve event pages with a custom HTML template, for every event or for one event.")}
        actions={sectionActions}
      >
        {body}
      </Section>

      <ConfirmDialog
        open={!!confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { if (confirm) { const snap = confirm; setConfirm(null); ctrl.apply(snap, describe); } }}
        title={confirmText ? confirmText.title : ""}
        body={confirmText ? confirmText.body : undefined}
        consequence={i18nT("journeys.customTemplates.confirmLive", "This is live as soon as you confirm.")}
        confirmLabel={i18nT("journeys.customTemplates.apply", "Apply")}
        cancelLabel={i18nT("journeys.customTemplates.cancel", "Cancel")}
      />

      {caps.author ? (
        <TemplateLibrary open={libraryOpen} onClose={() => { setLibraryOpen(false); ctrl.refresh(); }}
          initialKind={kind} events={eventsList} eventsLoading={st.catalog.status === "loading" || (props.scope === "landing" && st.catalog.status === "idle")}
          eventsError={st.catalog.status === "error" ? st.catalog.message ?? null : null} onRetryEvents={ctrl.loadCatalog} capabilities={caps} />
      ) : null}
    </>
  );
}
